/**
 * Analytics Dashboard Page
 * Displays analytics and consumer insights for manufacturers
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  analyticsService,
  AnalyticsOverview,
  GeographyData,
  ConsumptionTrend,
  Feedback,
  GasAnalytics,
  EnhancedSystemStatistics,
  HeatmapData,
  HeatmapPoint,
  FunnelAnalytics,
  BatchPerformance,
  MarketExpansionInsights,
  FraudAlert,
  PremiumAnalyticsPricing,
  PremiumAnalyticsReport
} from '../services/analytics.service';
import { GasConsumptionCharts } from '../components/GasConsumptionCharts';
import { GasThresholdAlerts } from '../components/GasThresholdAlerts';
import { EnhancedStatisticsDashboard } from '../components/EnhancedStatisticsDashboard';
import { CurrencySelector } from '../components/CurrencySelector';
import { MultiCurrencyDisplay } from '../components/MultiCurrencyDisplay';
import { Button, FilterBar, MetricCard, Modal, ModalFooter, PageHeader, StatusBadge } from '../components/ui';
import { productService, ProductBatch } from '../services/product.service';
import { useAuthStore } from '../store/authStore';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

const HEATMAP_SCAN_GRADIENT = {
  0.2: '#b7e4f8',
  0.4: '#5ab0f2',
  0.6: '#1f73e8',
  0.8: '#1b4f9c',
  1.0: '#102b61'
};

const HEATMAP_FRAUD_GRADIENT = {
  0.2: '#ffd5c0',
  0.4: '#ff9c66',
  0.6: '#ff5b3e',
  0.8: '#e02b20',
  1.0: '#8d0f0f'
};

type HeatLayerProps = {
  points: HeatmapPoint[];
  radius: number;
  blur: number;
  gradient: Record<number, string>;
};

const HeatmapLayer: React.FC<HeatLayerProps> = ({ points, radius, blur, gradient }) => {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;
    const maxCount = Math.max(...points.map((p) => p.count), 1);
    const heatPoints = points.map((p) => [p.lat, p.lng, Math.max(0.1, p.count / maxCount)]);
    const layer = (L as any).heatLayer(heatPoints, {
      radius,
      blur,
      maxZoom: 10,
      gradient
    });
    layer.addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, points, radius, blur, gradient]);

  return null;
};

const MapAutoFit: React.FC<{ points: HeatmapPoint[] }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 8 });
  }, [map, points]);

  return null;
};

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [funnel, setFunnel] = useState<FunnelAnalytics | null>(null);
  const [batchPerformance, setBatchPerformance] = useState<BatchPerformance[]>([]);
  const [marketInsights, setMarketInsights] = useState<MarketExpansionInsights | null>(null);
  const [geography, setGeography] = useState<GeographyData[]>([]);
  const [trends, setTrends] = useState<ConsumptionTrend[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [gasAnalytics, setGasAnalytics] = useState<GasAnalytics | null>(null);
  const [systemStats, setSystemStats] = useState<EnhancedSystemStatistics | null>(null);
  const [fraudAlerts, setFraudAlerts] = useState<FraudAlert[]>([]);
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [showScanHeatmap, setShowScanHeatmap] = useState(true);
  const [showFraudHeatmap, setShowFraudHeatmap] = useState(true);
  const [premiumReport, setPremiumReport] = useState<PremiumAnalyticsReport | null>(null);
  const [premiumPricing, setPremiumPricing] = useState<PremiumAnalyticsPricing | null>(null);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumError, setPremiumError] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(30);
  const [batches, setBatches] = useState<ProductBatch[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('all');
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<'HBAR' | 'USD' | 'INR'>('USD');
  const [activeTab, setActiveTab] = useState<'consumer' | 'gas' | 'system'>('consumer');
  const [searchTerm, setSearchTerm] = useState('');

  // Check if user is admin (roleLevel -1)
  const isAdmin = user?.roleLevel === -1;
  const isOperationManager = !!user?.isOperationManager;
  const canPurchasePremium = user?.roleLevel === 0 && !isOperationManager;

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, selectedBatchId, anomalyOnly, premiumReport]);

  useEffect(() => {
    if (!user?.manufacturerId) return;
    loadBatches();
  }, [user?.manufacturerId]);

  useEffect(() => {
    if (!user?.manufacturerId) return;
    loadPremiumStatus();
  }, [user?.manufacturerId, canPurchasePremium]);

  const activeFilters = useMemo(
    () => ({
      batchId: selectedBatchId !== 'all' ? selectedBatchId : undefined,
      anomalyOnly
    }),
    [selectedBatchId, anomalyOnly]
  );
  const premiumActive = !!premiumReport;

  useEffect(() => {
    if (!premiumActive) {
      setShowFraudHeatmap(false);
    }
  }, [premiumActive]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      setHeatmapError(null);

      const [
        overviewData,
        trendsData,
        feedbackData,
        gasData,
        statsData,
        heatmap
      ] = await Promise.all([
        analyticsService.getOverview(activeFilters),
        analyticsService.getConsumptionTrends(dateRange, activeFilters),
        analyticsService.getRecentFeedback(5, activeFilters),
        // Only load gas analytics for admin users
        isAdmin ? analyticsService.getGasAnalytics().catch(() => null) : Promise.resolve(null),
        // Only load system stats for admin users
        isAdmin ? analyticsService.getSystemStatistics().catch(() => null) : Promise.resolve(null),
        analyticsService.getHeatmapData(dateRange, activeFilters).catch(() => null)
      ]);

      setOverview(overviewData);
      setTrends(trendsData);
      setFeedback(feedbackData);
      setGasAnalytics(gasData);
      setSystemStats(statsData);

      if (heatmap) {
        setHeatmapData(heatmap);
      } else {
        setHeatmapData(null);
        setHeatmapError('Unable to load map data');
      }

      if (premiumActive) {
        try {
          setPremiumError(null);
          const [
            funnelData,
            batchPerformanceData,
            marketInsightsData,
            fraudAlertsData,
            geographyData
          ] = await Promise.all([
            analyticsService.getFunnelAnalytics(dateRange, activeFilters),
            analyticsService.getBatchPerformance(10, dateRange, activeFilters),
            analyticsService.getMarketInsights(dateRange, activeFilters),
            analyticsService.getFraudAlerts(activeFilters),
            analyticsService.getGeography(10, activeFilters)
          ]);

          setFunnel(funnelData || null);
          setBatchPerformance(batchPerformanceData || []);
          setMarketInsights(marketInsightsData || null);
          setFraudAlerts(fraudAlertsData || []);
          setGeography(geographyData || []);
        } catch (premiumErr: any) {
          setPremiumError(premiumErr?.response?.data?.error || 'Failed to load premium analytics data');
          setFunnel(null);
          setBatchPerformance([]);
          setMarketInsights(null);
          setFraudAlerts([]);
          setGeography([]);
        }
      } else {
        setFunnel(null);
        setBatchPerformance([]);
        setMarketInsights(null);
        setFraudAlerts([]);
        setGeography([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    if (!user?.manufacturerId) return;
    try {
      setBatchLoading(true);
      const data = await productService.getManufacturerBatches(user.manufacturerId, 100);
      setBatches(data || []);
      if (selectedBatchId !== 'all' && !data.some(batch => batch.id === selectedBatchId)) {
        setSelectedBatchId('all');
      }
    } catch (err) {
      console.error('Failed to load batches for analytics filters', err);
      setBatches([]);
    } finally {
      setBatchLoading(false);
    }
  };

  const loadPremiumStatus = async () => {
    if (!user?.manufacturerId) return;
    try {
      setPremiumLoading(true);
      setPremiumError(null);
      const [reportResponse, pricingResponse] = await Promise.all([
        analyticsService.getPremiumReport().catch(() => ({ active: false, report: undefined })),
        canPurchasePremium ? analyticsService.getPremiumPricing().catch(() => null) : Promise.resolve(null)
      ]);

      setPremiumReport(reportResponse.active ? reportResponse.report ?? null : null);
      setPremiumPricing(pricingResponse);
    } catch (err: any) {
      setPremiumError(err.response?.data?.error || 'Failed to load premium analytics status');
      setPremiumReport(null);
      setPremiumPricing(null);
    } finally {
      setPremiumLoading(false);
    }
  };

  const openPremiumModal = () => {
    if (premiumActive) return;
    setConsentAccepted(false);
    setShowPremiumModal(true);
  };

  const handlePurchasePremium = async () => {
    if (!premiumPricing || premiumActive) return;
    try {
      setPurchaseLoading(true);
      setPremiumError(null);
      const result = await analyticsService.purchasePremiumReport({
        days: dateRange,
        filters: activeFilters,
        consentAccepted: true
      });
      setPremiumReport(result.report);
      setShowPremiumModal(false);
    } catch (err: any) {
      setPremiumError(err.response?.data?.error || err.message || 'Failed to purchase premium analytics');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleCancelPremium = async () => {
    if (!premiumActive || !canPurchasePremium) return;
    if (!confirm('Cancel premium analytics subscription? Access remains until the current period ends.')) return;
    try {
      setCancelLoading(true);
      setPremiumError(null);
      const result = await analyticsService.cancelPremiumReport();
      setPremiumReport(result.report ?? premiumReport);
    } catch (err: any) {
      setPremiumError(err.response?.data?.error || err.message || 'Failed to cancel premium analytics');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatPricingAmount = (amount: number) => {
    if (!premiumPricing?.currency) {
      return amount.toFixed(2);
    }
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: premiumPricing.currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  };

  const scanPoints = heatmapData?.scanPoints ?? [];
  const fraudPoints = heatmapData?.fraudPoints ?? [];
  const hasHeatmapData = scanPoints.length > 0 || fraudPoints.length > 0;
  const canUnlockPremium = canPurchasePremium && !premiumActive;
  const baseCoverageLabel = useMemo(() => {
    if (premiumPricing?.baseCoverageMonths) {
      return `${premiumPricing.baseCoverageMonths} month${premiumPricing.baseCoverageMonths > 1 ? 's' : ''}`;
    }
    if (premiumPricing?.validityDays) {
      return `${premiumPricing.validityDays} days`;
    }
    return 'the initial period';
  }, [premiumPricing]);

  const currentBatchLabel = useMemo(() => {
    if (selectedBatchId === 'all') {
      return 'All batches';
    }
    const batch = batches.find((item) => item.id === selectedBatchId);
    return batch?.batchTitle || batch?.productName || selectedBatchId;
  }, [selectedBatchId, batches]);
  const topScanHours = useMemo(() => {
    if (!marketInsights) return [];
    return [...marketInsights.scanHourDistribution]
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .filter((entry) => entry.count > 0);
  }, [marketInsights]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (...values: Array<string | number | null | undefined>) =>
    normalizedSearch === '' ||
    values.some((value) => String(value ?? '').toLowerCase().includes(normalizedSearch));

  const filteredBatchOptions = useMemo(
    () => {
      if (normalizedSearch === '') {
        return batches;
      }
      const matched = batches.filter((batch) =>
        matchesSearch(batch.batchTitle, batch.productName, batch.id)
      );
      if (selectedBatchId === 'all' || matched.some((batch) => batch.id === selectedBatchId)) {
        return matched;
      }
      const selectedBatch = batches.find((batch) => batch.id === selectedBatchId);
      return selectedBatch ? [selectedBatch, ...matched] : matched;
    },
    [batches, normalizedSearch, selectedBatchId]
  );

  const filteredBatchPerformance = useMemo(
    () =>
      batchPerformance.filter((batch) =>
        matchesSearch(batch.batchTitle, batch.productName, batch.batchId)
      ),
    [batchPerformance, normalizedSearch]
  );

  const filteredGrowthMarkets = useMemo(
    () =>
      marketInsights?.topGrowthMarkets.filter((market) =>
        matchesSearch(market.city, market.state)
      ) ?? [],
    [marketInsights, normalizedSearch]
  );

  const filteredGeography = useMemo(
    () => geography.filter((geo) => matchesSearch(geo.city, geo.state)),
    [geography, normalizedSearch]
  );

  const filteredFeedback = useMemo(
    () => feedback.filter((item) => matchesSearch(item.productName, item.feedback, item.city)),
    [feedback, normalizedSearch]
  );

  const filteredFraudAlerts = useMemo(
    () =>
      fraudAlerts.filter((alert) =>
        matchesSearch(alert.productName, alert.batchId, alert.serialNumber, alert.lastCity)
      ),
    [fraudAlerts, normalizedSearch]
  );

  const filteredScanPoints = useMemo(
    () => scanPoints.filter((point) => matchesSearch(point.city, point.state)),
    [scanPoints, normalizedSearch]
  );

  const filteredFraudPoints = useMemo(
    () => fraudPoints.filter((point) => matchesSearch(point.city, point.state)),
    [fraudPoints, normalizedSearch]
  );

  const mapPoints = useMemo(() => {
    const points = [];
    if (showScanHeatmap) points.push(...filteredScanPoints);
    if (showFraudHeatmap) points.push(...filteredFraudPoints);
    return points;
  }, [filteredFraudPoints, filteredScanPoints, showFraudHeatmap, showScanHeatmap]);

  const topScanPoints = useMemo(
    () => [...filteredScanPoints].sort((a, b) => b.count - a.count).slice(0, 3),
    [filteredScanPoints]
  );
  const topFraudPoints = useMemo(
    () => [...filteredFraudPoints].sort((a, b) => b.count - a.count).slice(0, 3),
    [filteredFraudPoints]
  );

  const availableViews = useMemo(() => {
    const views: Array<{ value: string; label: string }> = [
      { value: 'consumer', label: 'Consumer Analytics' },
    ];
    if (isAdmin && gasAnalytics) {
      views.push({ value: 'gas', label: 'Gas Tracking' });
    }
    if (isAdmin && systemStats) {
      views.push({ value: 'system', label: 'System Statistics' });
    }
    return views;
  }, [gasAnalytics, isAdmin, systemStats]);

  useEffect(() => {
    if (!availableViews.some((view) => view.value === activeTab)) {
      setActiveTab('consumer');
    }
  }, [activeTab, availableViews]);

  const handleExport = async () => {
    try {
      if (activeTab === 'gas') {
        await analyticsService.exportGasAnalyticsToCSV();
      } else {
        if (!premiumActive) {
          alert('Premium analytics access is required to export this report.');
          return;
        }
        await analyticsService.exportToCSV(false, activeFilters);
      }
    } catch (err: any) {
      alert('Failed to export data: ' + (err.response?.data?.error || err.message));
    }
  };

  const exportDisabled = activeTab !== 'gas' && !premiumActive;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={loadAnalytics}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Analytics Dashboard"
          description="Consumer insights, gas tracking, and system performance"
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/manufacturer')}>
                Back to Dashboard
              </Button>
              {activeTab === 'gas' && (
                <CurrencySelector
                  selectedCurrency={selectedCurrency}
                  onCurrencyChange={setSelectedCurrency}
                />
              )}
              <Button onClick={handleExport} disabled={exportDisabled}>
                Export CSV
              </Button>
            </div>
          }
        />

        <FilterBar
          className="mt-6"
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by product, batch, city, serial, or feedback..."
          statusOptions={[
            { value: 'all', label: 'All Scans' },
            { value: 'anomaly-only', label: 'Anomalies Only' },
          ]}
          selectedStatus={anomalyOnly ? 'anomaly-only' : 'all'}
          onStatusChange={(value) => setAnomalyOnly(value === 'anomaly-only')}
          sortOptions={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
            { value: '365', label: 'Last year' },
          ]}
          selectedSort={String(dateRange)}
          onSortChange={(value) => setDateRange(Number(value))}
          viewOptions={availableViews}
          selectedView={activeTab}
          onViewChange={(value) => setActiveTab(value as 'consumer' | 'gas' | 'system')}
        />

        <section className="mb-6 mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Batch
              <select
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                disabled={batchLoading}
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="all">All batches</option>
                {batchLoading && <option value="loading">Loading batches...</option>}
                {!batchLoading &&
                  filteredBatchOptions.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.batchTitle || batch.productName || batch.id}
                    </option>
                  ))}
              </select>
            </label>
            <div className="flex items-end">
              <StatusBadge tone={premiumActive ? 'active' : 'muted'}>
                Premium {premiumActive ? 'Active' : 'Locked'}
              </StatusBadge>
            </div>
          </div>
        </section>

        {/* Consumer Analytics Tab */}
        {activeTab === 'consumer' && overview && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <MetricCard
                title="Total Scans"
                value={overview.totalScans.toLocaleString()}
                icon="📊"
                tone="blue"
              />
              <MetricCard
                title="Total Consumptions"
                value={overview.totalConsumptions.toLocaleString()}
                icon="✅"
                tone="green"
              />
              <MetricCard
                title="Total Products"
                value={overview.totalProducts.toLocaleString()}
                icon="📦"
                tone="purple"
              />
              <MetricCard
                title="Engagement Rate"
                value={`${overview.engagementRate.toFixed(1)}%`}
                icon="📈"
                tone="indigo"
              />
              <MetricCard
                title="Average Rating"
                value={overview.avgRating.toFixed(1)}
                trendLabel="out of 5.0"
                icon="⭐"
                tone="yellow"
              />
              <MetricCard
                title="Fraud Alerts"
                value={overview.fraudAlerts.toString()}
                icon="⚠️"
                tone="red"
              />
            </div>

            {/* Premium Analytics */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Premium Analytics</h2>
                  <p className="text-sm text-gray-600">
                    Unlock growth, fraud, and market insights. Base access covers {baseCoverageLabel}, then auto-charges monthly until you cancel the subscription.
                  </p>
                  {premiumActive && premiumReport?.validUntil && (
                    <p className="text-xs text-gray-500 mt-1">
                      Active until {new Date(premiumReport.validUntil).toLocaleDateString()}
                    </p>
                  )}
                  {premiumError && (
                    <p className="text-xs text-red-600 mt-1">{premiumError}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    premiumActive
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'
                  }`}>
                    {premiumActive ? 'Active' : 'Locked'}
                  </span>
                  {premiumActive && canPurchasePremium && (
                    <button
                      onClick={handleCancelPremium}
                      disabled={cancelLoading}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                        cancelLoading
                          ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                  )}
                  {canUnlockPremium ? (
                    <button
                      onClick={openPremiumModal}
                      disabled={premiumLoading || !premiumPricing}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        premiumLoading || !premiumPricing
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {premiumPricing ? `Unlock for ${premiumPricing.formatted}` : 'Pricing unavailable'}
                    </button>
                  ) : premiumActive ? (
                    <span className="text-sm text-gray-500">Premium access active</span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      Ask your admin to purchase access
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Growth Funnel */}
            {premiumActive && funnel ? (
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Growth Funnel</h2>
                    <p className="text-sm text-gray-600">
                      Scan metrics use last {dateRange} days. Product totals are all-time.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricCard
                    title="Total Products"
                    value={funnel.totalProducts.toLocaleString()}
                    trendLabel="All time"
                    icon="P"
                    tone="blue"
                  />
                  <MetricCard
                    title="Activated Products"
                    value={funnel.activatedProducts.toLocaleString()}
                    trendLabel="All time"
                    icon="A"
                    tone="green"
                  />
                  <MetricCard
                    title="Consumed Products"
                    value={funnel.consumedProducts.toLocaleString()}
                    trendLabel="All time"
                    icon="C"
                    tone="purple"
                  />
                  <MetricCard
                    title="Scanned Products"
                    value={funnel.scannedProducts.toLocaleString()}
                    trendLabel={`Last ${dateRange} days`}
                    icon="S"
                    tone="indigo"
                  />
                  <MetricCard
                    title="Scan Events"
                    value={funnel.scanEvents.toLocaleString()}
                    trendLabel={`Last ${dateRange} days`}
                    icon="#"
                    tone="yellow"
                  />
                  <MetricCard
                    title="Repeat Scans (2+)"
                    value={funnel.repeatScannedProducts.toLocaleString()}
                    trendLabel={`Last ${dateRange} days`}
                    icon="R"
                    tone="red"
                  />
                  <MetricCard
                    title="Feedback Scans"
                    value={funnel.feedbackScans.toLocaleString()}
                    trendLabel={`Last ${dateRange} days`}
                    icon="F"
                    tone="green"
                  />
                </div>
              </div>
            ) : (
              <PremiumLockedCard
                title="Growth Funnel"
                description="Unlock premium analytics to view growth funnel metrics."
                action={canUnlockPremium ? (
                  <button
                    onClick={openPremiumModal}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Unlock Premium
                  </button>
                ) : undefined}
              />
            )}

            {/* Batch Performance */}
            {premiumActive ? (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Batch Performance</h2>
                    <p className="text-sm text-gray-600">Scan metrics use last {dateRange} days.</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {filteredBatchPerformance.length} batches
                  </span>
                </div>
                {filteredBatchPerformance.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">No batch performance data available</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" aria-label="Batch performance">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Batch</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Products</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Activated</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Consumed</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Scans</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Repeat</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Anomalies</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Rating</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Scan</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBatchPerformance.map((batch) => (
                          <tr key={batch.batchId}>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">
                                {batch.batchTitle || batch.productName}
                              </div>
                              <div className="text-xs text-gray-500">Batch {batch.batchId.slice(0, 8)}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.totalProducts.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.activatedProducts.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.consumedProducts.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div>{batch.scannedProducts.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">{batch.scanEvents.toLocaleString()} scans</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.repeatScannedProducts.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.anomalyScans.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.avgRating !== null ? batch.avgRating.toFixed(1) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {batch.lastScanAt ? new Date(batch.lastScanAt).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <PremiumLockedCard
                title="Batch Performance"
                description="Unlock premium analytics to view batch performance details."
                action={canUnlockPremium ? (
                  <button
                    onClick={openPremiumModal}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Unlock Premium
                  </button>
                ) : undefined}
              />
            )}

            {/* Market Expansion Insights */}
            {premiumActive && marketInsights ? (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Market Expansion Insights</h2>
                    <p className="text-sm text-gray-600">
                      Highlights from the last {dateRange} days with growth vs the previous period.
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    {filteredGrowthMarkets.length} markets
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <MetricCard
                    title="Repeat Scan Rate"
                    value={`${marketInsights.repeatScanRate.repeatRate.toFixed(1)}%`}
                    trendLabel={`${marketInsights.repeatScanRate.repeatScannedProducts.toLocaleString()} of ${marketInsights.repeatScanRate.scannedProducts.toLocaleString()} products`}
                    icon="R"
                    tone="blue"
                  />
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Peak Scan Hours</h3>
                    {topScanHours.length === 0 ? (
                      <p className="text-sm text-gray-500">No scan activity yet</p>
                    ) : (
                      <div className="space-y-2">
                        {topScanHours.map((entry) => (
                          <div key={entry.hour} className="flex items-center justify-between text-sm text-gray-700">
                            <span>{entry.hour}:00</span>
                            <span className="font-semibold text-gray-900">{entry.count.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Scan Spread</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                      <div>
                        <p className="text-xs text-gray-500">Total scans</p>
                        <p className="font-semibold text-gray-900">
                          {marketInsights.scanHourDistribution.reduce((sum, entry) => sum + entry.count, 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Active hours</p>
                        <p className="font-semibold text-gray-900">
                          {marketInsights.scanHourDistribution.filter((entry) => entry.count > 0).length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {filteredGrowthMarkets.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">No growth market data available</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" aria-label="Growth markets">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">City</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Current</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Previous</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Growth</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredGrowthMarkets.map((market) => (
                          <tr key={`${market.city}-${market.state}`}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="font-medium">{market.city}</div>
                              <div className="text-xs text-gray-500">{market.state}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {market.currentScans.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {market.previousScans.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {market.isNewMarket ? (
                                <span className="text-green-600 font-semibold">New</span>
                              ) : market.growthPct !== null ? (
                                <span className={market.growthPct >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {market.growthPct.toFixed(1)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <PremiumLockedCard
                title="Market Expansion Insights"
                description="Unlock premium analytics to see market growth and repeat scan insights."
                action={canUnlockPremium ? (
                  <button
                    onClick={openPremiumModal}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Unlock Premium
                  </button>
                ) : undefined}
              />
            )}

            {/* Consumption Trends Chart */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Consumption Trends</h2>
              {trends.length > 0 ? (
                <div className="space-y-2">
                  {trends.map((trend) => (
                    <div key={trend.date} className="flex items-center gap-4">
                      <span className="text-sm text-gray-600 w-24">{trend.date}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-6">
                        <div
                          className="bg-blue-600 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{
                            width: `${Math.min((trend.count / Math.max(...trends.map(t => t.count))) * 100, 100)}%`
                          }}
                        >
                          <span className="text-xs text-white font-medium">{trend.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No consumption data available</p>
              )}
            </div>

            {/* Scan & Fraud Heatmap */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Scan & Fraud Map</h2>
                  <p className="text-sm text-gray-600">
                    Aggregated heatmap of scan locations (last {dateRange} days). Powered by OpenStreetMap.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={showScanHeatmap}
                      onChange={(e) => setShowScanHeatmap(e.target.checked)}
                      aria-label="Toggle scan heatmap"
                    />
                    <span>Show scans</span>
                  </label>
                  {premiumActive ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={showFraudHeatmap}
                        onChange={(e) => setShowFraudHeatmap(e.target.checked)}
                        aria-label="Toggle fraud heatmap"
                      />
                      <span>Show fraud</span>
                    </label>
                  ) : (
                    <span className="text-xs text-gray-500">Unlock premium to view fraud hotspots.</span>
                  )}
                </div>
              </div>

              {heatmapError && (
                <p className="text-sm text-red-600 mb-4">{heatmapError}</p>
              )}

              {!heatmapError && !hasHeatmapData && (
                <p className="text-gray-500 text-center py-8">No location data available for this period</p>
              )}

              {!heatmapError && hasHeatmapData && mapPoints.length === 0 && (
                <p className="text-gray-500 text-center py-8">Enable at least one layer to view the map</p>
              )}

              {!heatmapError && mapPoints.length > 0 && (
                <div className="h-[360px] w-full rounded-lg overflow-hidden border border-gray-200">
                  <MapContainer
                    center={[22.9734, 78.6569]}
                    zoom={4}
                    scrollWheelZoom
                    className="h-full w-full"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapAutoFit points={mapPoints} />
                    {showScanHeatmap && filteredScanPoints.length > 0 && (
                      <HeatmapLayer
                        points={filteredScanPoints}
                        radius={28}
                        blur={20}
                        gradient={HEATMAP_SCAN_GRADIENT}
                      />
                    )}
                    {showFraudHeatmap && filteredFraudPoints.length > 0 && (
                      <HeatmapLayer
                        points={filteredFraudPoints}
                        radius={22}
                        blur={18}
                        gradient={HEATMAP_FRAUD_GRADIENT}
                      />
                    )}
                  </MapContainer>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="rounded-lg border border-gray-200 p-3">
                  <h3 className="font-semibold text-gray-800 mb-2">Top scan hotspots</h3>
                  {topScanPoints.length === 0 ? (
                    <p className="text-gray-500">No scan hotspots available</p>
                  ) : (
                    <div className="space-y-2">
                      {topScanPoints.map((point, index) => (
                        <div key={`${point.lat}-${point.lng}-${index}`} className="flex justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {point.city || 'Unknown city'}{point.state ? `, ${point.state}` : ''}
                            </div>
                            <div className="text-xs text-gray-500">
                              {point.lat.toFixed(3)}, {point.lng.toFixed(3)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">{point.count} scans</div>
                            {point.lastScanAt && (
                              <div className="text-xs text-gray-500">
                                {new Date(point.lastScanAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                </div>
                {premiumActive ? (
                  <div className="rounded-lg border border-gray-200 p-3">
                    <h3 className="font-semibold text-gray-800 mb-2">Top fraud hotspots</h3>
                    {topFraudPoints.length === 0 ? (
                      <p className="text-gray-500">No fraud hotspots available</p>
                    ) : (
                      <div className="space-y-2">
                        {topFraudPoints.map((point, index) => (
                          <div key={`${point.lat}-${point.lng}-${index}`} className="flex justify-between gap-3">
                            <div>
                              <div className="font-medium text-gray-900">
                                {point.city || 'Unknown city'}{point.state ? `, ${point.state}` : ''}
                              </div>
                              <div className="text-xs text-gray-500">
                                {point.lat.toFixed(3)}, {point.lng.toFixed(3)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">{point.count} scans</div>
                              {point.lastScanAt && (
                                <div className="text-xs text-gray-500">
                                  {new Date(point.lastScanAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 p-3">
                    <h3 className="font-semibold text-gray-800 mb-2">Top fraud hotspots</h3>
                    <p className="text-gray-500">Unlock premium analytics to view fraud hotspots.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Geographic Distribution */}
              {premiumActive ? (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Cities</h2>
                  {filteredGeography.length > 0 ? (
                    <div className="space-y-3">
                      {filteredGeography.map((geo, index) => (
                        <div key={`${geo.city}-${geo.state}`} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                            <div>
                              <p className="font-medium text-gray-900">{geo.city}</p>
                              <p className="text-sm text-gray-500">{geo.state}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{geo.scanCount} scans</p>
                            <p className="text-sm text-gray-500">{geo.consumptionCount} consumed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No geographic data available</p>
                  )}
                </div>
              ) : (
                <PremiumLockedCard
                  title="Top Cities"
                  description="Unlock premium analytics to view geographic insights."
                  action={canUnlockPremium ? (
                    <button
                      onClick={openPremiumModal}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Unlock Premium
                    </button>
                  ) : undefined}
                />
              )}

              {/* Recent Feedback */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Feedback</h2>
                {filteredFeedback.length > 0 ? (
                  <div className="space-y-4">
                    {filteredFeedback.map((item, index) => (
                      <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-gray-900">{item.productName}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={i < item.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                ⭐
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{item.feedback}</p>
                        <p className="text-xs text-gray-400">
                          {item.city} • {new Date(item.scannedAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No feedback available</p>
                )}
              </div>
            </div>

            {/* Fraud Alerts Detail */}
            {premiumActive ? (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Fraud Alerts</h2>
                  <p className="text-sm text-gray-600">Suspicious scan activity for your products</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                  {filteredFraudAlerts.length} alerts
                </span>
              </div>
              {filteredFraudAlerts.length === 0 ? (
                <p className="text-gray-500 text-center py-6">No fraud alerts detected</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200" aria-label="Fraud alerts">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product / Batch</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Scans</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cities</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Scan</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Flag</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFraudAlerts.map((alert) => (
                        <tr key={alert.productId}>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{alert.productName}</div>
                            <div className="text-xs text-gray-500">SN {alert.serialNumber} · Batch {alert.batchId.slice(0, 8)}</div>
                            {alert.outerQrToken && (
                              <div className="text-[11px] text-gray-400 mt-1">
                                QR: {alert.outerQrToken.slice(0, 12)}...
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {alert.scanCount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {alert.uniqueCities} {alert.lastCity ? `(${alert.lastCity})` : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {alert.lastScan ? new Date(alert.lastScan).toLocaleString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {alert.flagRiskLevel ? (
                              <div className="flex flex-col">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  alert.flagRiskLevel === 'high'
                                    ? 'bg-red-100 text-red-800'
                                    : alert.flagRiskLevel === 'medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {alert.flagRiskLevel} · {alert.flagRiskScore ?? 'n/a'}
                                </span>
                                {alert.flagReasons && alert.flagReasons.length > 0 && (
                                  <span className="text-[11px] text-gray-600 mt-1">
                                    {alert.flagReasons.slice(0, 2).join('; ')}
                                  </span>
                                )}
                                {alert.flagStatus && (
                                  <span className="text-[11px] text-gray-500 mt-1">
                                    Status: {alert.flagStatus}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-sm">No flag</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            ) : (
              <PremiumLockedCard
                title="Fraud Alerts"
                description="Unlock premium analytics to view fraud alert details."
                action={canUnlockPremium ? (
                  <button
                    onClick={openPremiumModal}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Unlock Premium
                  </button>
                ) : undefined}
              />
            )}
          </>
        )}

        {/* Gas Tracking Tab */}
        {activeTab === 'gas' && gasAnalytics && (
          <div className="space-y-6">
            {/* Gas Analytics Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Gas Used"
                value={gasAnalytics.totalGasUsed.toLocaleString()}
                icon="⛽"
                tone="blue"
              />
              <MetricCard
                title={`Total Cost (${selectedCurrency})`}
                value={
                  selectedCurrency === 'HBAR' ? gasAnalytics.totalCostHBAR.toFixed(4) :
                  selectedCurrency === 'USD' ? `$${gasAnalytics.totalCostUSD.toFixed(2)}` :
                  `₹${gasAnalytics.totalCostINR.toFixed(2)}`
                }
                icon="💰"
                tone="green"
              />
              <MetricCard
                title="Total Transactions"
                value={gasAnalytics.operationBreakdown.reduce((sum, op) => sum + op.transactionCount, 0).toLocaleString()}
                icon="📊"
                tone="purple"
              />
              <MetricCard
                title={`Avg Cost/Transaction (${selectedCurrency})`}
                value={
                  selectedCurrency === 'HBAR' ? gasAnalytics.averageCostPerOperation.hbar.toFixed(4) :
                  selectedCurrency === 'USD' ? `$${gasAnalytics.averageCostPerOperation.usd.toFixed(4)}` :
                  `₹${gasAnalytics.averageCostPerOperation.inr.toFixed(2)}`
                }
                icon="📈"
                tone="indigo"
              />
            </div>

            {/* Gas Threshold Alerts */}
            <GasThresholdAlerts selectedCurrency={selectedCurrency} />

            {/* Gas Consumption Charts */}
            <GasConsumptionCharts 
              selectedCurrency={selectedCurrency}
              filters={{
                startDate: new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              }}
            />

            {/* Multi-Currency Display */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Breakdown by Currency</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Total Gas Costs</p>
                  <MultiCurrencyDisplay
                    amount={gasAnalytics.totalCostHBAR}
                    baseCurrency="HBAR"
                    showAllCurrencies={true}
                    size="lg"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Average Cost per Transaction</p>
                  <MultiCurrencyDisplay
                    amount={gasAnalytics.averageCostPerOperation.hbar}
                    baseCurrency="HBAR"
                    showAllCurrencies={true}
                    size="md"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Statistics Tab */}
        {activeTab === 'system' && systemStats && (
          <EnhancedStatisticsDashboard />
        )}

        <Modal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          title="Unlock Premium Analytics"
          size="md"
        >
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              Generate a premium analytics report for the current filters. Access stays active and auto-renews monthly
              until you cancel the subscription.
            </p>
            <div className="rounded-lg bg-gray-50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Price</span>
                <span className="font-semibold text-gray-900">
                  {premiumPricing?.formatted || 'Pricing unavailable'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Filters</span>
                <span className="text-gray-900">
                  {currentBatchLabel} / {anomalyOnly ? 'anomalies only' : 'all scans'} / last {dateRange} days
                </span>
              </div>
            </div>
            {premiumPricing && (premiumPricing.baseCoverageMonths !== undefined || premiumPricing.monthlySurchargePercent !== undefined) && (
              <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-900 space-y-1">
                <div className="font-semibold">Pricing plan</div>
                {premiumPricing.baseCoverageMonths !== undefined && (
                  <p>
                    Base price covers the first {premiumPricing.baseCoverageMonths} months.
                  </p>
                )}
                {premiumPricing.monthlySurchargePercent !== undefined && premiumPricing.monthlySurchargePercent > 0 && (
                  <p>
                    After that, auto-charge +{premiumPricing.monthlySurchargePercent}% per month (~
                    {formatPricingAmount(premiumPricing.amount * (premiumPricing.monthlySurchargePercent / 100))} per month at
                    current price) until you cancel.
                  </p>
                )}
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                aria-label="Consent to premium analytics charges"
              />
              <span>{premiumPricing?.consentText || 'I consent to premium analytics report generation.'}</span>
            </label>
          </div>
          <ModalFooter>
            <button
              onClick={() => setShowPremiumModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
              disabled={purchaseLoading}
            >
              Cancel
            </button>
            <button
              onClick={handlePurchasePremium}
              disabled={!consentAccepted || purchaseLoading || !premiumPricing}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                !consentAccepted || purchaseLoading || !premiumPricing
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {purchaseLoading ? 'Processing...' : 'Confirm and Pay'}
            </button>
          </ModalFooter>
        </Modal>

      </div>
    </div>
  );
};

interface PremiumLockedCardProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const PremiumLockedCard: React.FC<PremiumLockedCardProps> = ({ title, description, action }) => (
  <div className="bg-white rounded-lg shadow p-6 mb-6">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">
        Premium
      </span>
    </div>
    <p className="text-sm text-gray-600">{description || 'Premium analytics access is required.'}</p>
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export default AnalyticsDashboard;

